import type React from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface FieldProps {
	label: string;
	/** Rendered value. When nullish/empty a localized "not available" fallback is shown. */
	value: ReactNode;
}

const isEmptyValue = (value: ReactNode): boolean =>
	value == null || value === '' || (typeof value === 'number' && Number.isNaN(value));

/**
 * A label-above-value field used inside the request overview cards. Read-only.
 * Falls back to a muted "not available" label when the value is missing so the
 * reader can tell an unanswered field apart from an empty section.
 */
const Field = ({ label, value }: FieldProps): React.JSX.Element => {
	const { t } = useTranslation();
	const empty = isEmptyValue(value);

	return (
		<div className="flex flex-col gap-0.5">
			<dt className="text-xs text-muted-foreground">{label}</dt>
			<dd
				className={
					empty
						? 'text-sm italic text-muted-foreground/70'
						: 'text-sm font-semibold text-foreground'
				}
			>
				{empty ? t('requestOverview.notAvailable') : value}
			</dd>
		</div>
	);
};

export default Field;
